import { useNode } from '@craftjs/core';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  AlignHorizontalRight,
  AlignVerticalTop,
  AlignVerticalCenter,
  AlignVerticalBottom,
} from '@mui/icons-material';
import { ArrowForward, ArrowDownward } from '@mui/icons-material';
import useResponsiveValues from '@/components/editor/hooks/useResponsiveValues';
import { ResponsiveValue } from "@/components/editor/hooks/useResponsiveValues";

export enum Display {
  FLEX = 'flex',
  BLOCK = 'block',
  GRID = 'grid',
  INLINE = 'inline',
}

export enum FlexDirection {
  ROW = 'row',
  COLUMN = 'column',
}

export enum FlexAlign {
  START = 'start',
  CENTER = 'center',
  END = 'end',
  SPACE_BETWEEN = 'space-between',
  SPACE_AROUND = 'space-around',
}

export interface DisplayProps {
  display?: Display | ResponsiveValue<Display>;
  flexDirection?: FlexDirection | ResponsiveValue<FlexDirection>;
  justifyContent?: FlexAlign | ResponsiveValue<FlexAlign>;
  alignItems?: FlexAlign | ResponsiveValue<FlexAlign>;
  gap?: string | ResponsiveValue<string>;
  flexGrow?: string | ResponsiveValue<string>;
}

const DisplaySettings = () => {
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const responsive = useResponsiveValues();

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <InputLabel>Display</InputLabel>
        <RadioGroup
          row
          color="primary"
          value={responsive.getValue(props.display) ?? ''}
          defaultValue={Display.BLOCK}
          onChange={(_, value) => {
            setProp((props: DisplayProps) => {
              props.display = responsive.setValue(props.display, value);
              return props;
            });
          }}
        >
          <FormControlLabel
            control={<Radio />}
            value={Display.BLOCK}
            label="Bloque"
          />
          <FormControlLabel
            control={<Radio />}
            value={Display.INLINE}
            label="En línea"
          />
          <FormControlLabel
            control={<Radio />}
            value={Display.FLEX}
            label="Flex"
          />
          <FormControlLabel
            control={<Radio />}
            value={Display.GRID}
            label="Grid"
          />
        </RadioGroup>
      </Stack>

      {responsive.getValue(props.display) === Display.FLEX ? (
        <Stack spacing={1}>
          <Stack spacing={1} direction="row">
            <Stack spacing={1}>
              <InputLabel>Dirección</InputLabel>
              <ToggleButtonGroup
                color="primary"
                value={responsive.getValue(props.flexDirection) ?? ''}
                onChange={(_, value) => {
                  setProp((props: DisplayProps) => {
                    props.flexDirection = responsive.setValue(props.flexDirection, value);
                    return props;
                  });
                }}
                exclusive
                fullWidth
                size="small"
              >
                <ToggleButton value={FlexDirection.ROW}>
                  <ArrowForward fontSize="small" />
                </ToggleButton>
                <ToggleButton value={FlexDirection.COLUMN}>
                  <ArrowDownward fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack spacing={1}>
              <InputLabel>Horizontal</InputLabel>
              <ToggleButtonGroup
                color="primary"
                value={
                  responsive.getValue(props.flexDirection) === FlexDirection.ROW
                    ? responsive.getValue(props.justifyContent) ?? ''
                    : responsive.getValue(props.alignItems) ?? ''
                }
                onChange={(_, value) => {
                  setProp((props: DisplayProps) => {
                    if (responsive.getValue(props.flexDirection) === FlexDirection.ROW) {
                      props.justifyContent = responsive.setValue(props.justifyContent, value);
                    } else {
                      props.alignItems = responsive.setValue(props.alignItems, value);
                    }
                    return props;
                  });
                }}
                exclusive
                fullWidth
                size="small"
              >
                <ToggleButton value={FlexAlign.START}>
                  <AlignHorizontalLeft fontSize="small" />
                </ToggleButton>
                <ToggleButton value={FlexAlign.CENTER}>
                  <AlignHorizontalCenter fontSize="small" />
                </ToggleButton>
                <ToggleButton value={FlexAlign.END}>
                  <AlignHorizontalRight fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack spacing={1}>
              <InputLabel>Vertical</InputLabel>
              <ToggleButtonGroup
                color="primary"
                value={
                  responsive.getValue(props.flexDirection) === FlexDirection.ROW
                    ? responsive.getValue(props.alignItems)
                    : responsive.getValue(props.justifyContent)
                }
                onChange={(_, value) => {
                  setProp((props: DisplayProps) => {
                    if (responsive.getValue(props.flexDirection) === FlexDirection.ROW) {
                      props.alignItems = responsive.setValue(props.alignItems, value);
                    } else {
                      props.justifyContent = responsive.setValue(props.justifyContent, value);
                    }
                    return props;
                  });
                }}
                exclusive
                fullWidth
                size="small"
              >
                <ToggleButton value={FlexAlign.START}>
                  <AlignVerticalTop fontSize="small" />
                </ToggleButton>
                <ToggleButton value={FlexAlign.CENTER}>
                  <AlignVerticalCenter fontSize="small" />
                </ToggleButton>
                <ToggleButton value={FlexAlign.END}>
                  <AlignVerticalBottom fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
          <FormControl fullWidth={true} margin="normal" component="fieldset">
            <Stack direction={'row'} spacing={2}>
              <Slider
                step={8}
                marks
                min={8}
                max={100}
                value={Number(responsive.getValue(props.gap)) ?? 0}
                onChange={(_, value) =>
                  setProp((props: DisplayProps) => {
                    props.gap = responsive.setValue(props.gap, value.toString());
                    return props;
                  })
                }
              />
              <TextField
                size="small"
                value={responsive.getValue(props.height) ?? ''}
                onChange={(e) =>
                  setProp((props: DisplayProps) => {
                    props.gap = responsive.setValue(props.gap, e.target.value);
                    return props;
                  })
                }
              />
            </Stack>
          </FormControl>

          <TextField
            label="Crecimiento"
            size="small"
            value={responsive.getValue(props.flexGrow) ?? ''}
            onChange={(e) =>
              setProp((props: DisplayProps) => {
                props.flexGrow = responsive.setValue(props.flexGrow, e.target.value);
                return props;
              })
            }
          />
        </Stack>
      ) : null}
    </Stack>
  );
};

export default DisplaySettings;
